import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { getExceptionalStatusTranslationKey } from "src/utils/getExceptionalStatusTranslationKey";
import { memo } from "react";
import { useExceptionDraft } from "../contexts/ExceptionDraftContext";
import { DayExceptionsList } from "./DayExceptionsList";
import { useDayExceptions, useOverlappingExceptions } from "./useDayExceptions";

dayjs.extend(utc);

const PICKER_FORMAT = "MM-DD-YYYY HH:mm:ss";
const TIME_FORMAT = "MM-DD HH:mm:ss";

export const DayExceptionsPanel = memo(
  ({ dayStart, dayEnd }: { dayStart: Dayjs; dayEnd: Dayjs }) => {
    const { t } = useTenantTranslation();
    const {
      exceptionalStatuses,
      draft,
      patchDraft,
      openCreate,
      openEdit,
      requestDelete,
    } = useExceptionDraft();
    const dayExceptions = useDayExceptions({
      exceptionalStatuses,
      dayStart,
      dayEnd,
    });
    const overlapping = useOverlappingExceptions({
      exceptionalStatuses,
      startedAt: draft.startedAt,
      endedAt: draft.endedAt,
    });

    const canAdd =
      draft.startedAt !== null &&
      draft.endedAt !== null &&
      draft.startedAt.isBefore(draft.endedAt);
    const hasInvalidOrder =
      draft.startedAt !== null &&
      draft.endedAt !== null &&
      !draft.startedAt.isBefore(draft.endedAt);

    const addTooltip = hasInvalidOrder
      ? t("startMustBeBeforeEnd")
      : canAdd
        ? t("addExceptionalPeriod")
        : t("fillStartAndEnd");

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          bgcolor: "background.paper",
          borderRadius: 1,
          p: 1,
          overflow: "hidden",
        }}
      >
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <SafeDateTimePicker
            label={t("startedAt")}
            value={draft.startedAt}
            onChange={(value) => {
              patchDraft({ startedAt: value });
            }}
            ampm={false}
            format={PICKER_FORMAT}
            timezone="UTC"
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <SafeDateTimePicker
            label={t("endedAt")}
            value={draft.endedAt}
            onChange={(value) => {
              patchDraft({ endedAt: value });
            }}
            ampm={false}
            format={PICKER_FORMAT}
            timezone="UTC"
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          {canAdd && overlapping.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="warning.main">
                {t("exceptionOverlapsHint")}
              </Typography>
              {overlapping.map((status) => (
                <Typography
                  key={status.id}
                  variant="caption"
                  color="warning.main"
                  sx={{ pl: 1 }}
                >
                  {t(getExceptionalStatusTranslationKey(status.status))} ·{" "}
                  {dayjs.utc(status.started_at).format(TIME_FORMAT)} –{" "}
                  {dayjs.utc(status.ended_at).format(TIME_FORMAT)}
                </Typography>
              ))}
            </Stack>
          ) : null}
          <Stack direction="row" justifyContent="flex-end">
            <Tooltip title={addTooltip}>
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={openCreate}
                  disabled={!canAdd}
                  aria-label={t("addExceptionalPeriod")}
                >
                  <AddCircleIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <DayExceptionsList
          statuses={dayExceptions}
          onEdit={openEdit}
          onDelete={requestDelete}
        />
      </Box>
    );
  }
);
DayExceptionsPanel.displayName = "DayExceptionsPanel";
